import { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import type { EChartsOption, ECharts } from 'echarts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useProcessedSkuCalendarData } from '@/hooks/useSkuCalendarData';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Box, TrendingUp, Package } from 'lucide-react';
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

// 格式化数字为千位分隔
const formatNumber = (num: number) => {
  return new Intl.NumberFormat('zh-CN').format(num);
};

// 获取一年中的第几周
const getWeekOfYear = (dateStr: string) => {
  const date = new Date(dateStr);
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);
  return weekNumber;
};

// 格式化日期显示
const formatDateWithWeek = (dateStr: string) => {
  const weekNumber = getWeekOfYear(dateStr);
  return `${dateStr}\n( W${weekNumber} )`;
};

// 格式化日期显示
const formatDate = (dateStr: string) => {
  return dateStr.split('T')[0];
};

// 表头配置
const TABLE_HEADERS = [
  { key: 'brandId', label: '品牌ID' },
  { key: 'brandName', label: '品牌名称' },
  { key: 'spuCode', label: 'SPU编码' },
  { key: 'spuName', label: 'SPU名称' },
  { key: 'planDate', label: '计划日期' },
  { key: 'planQuantity', label: '计划数量' },
] as const;

// 品牌颜色映射
const BRAND_COLORS = {
   '3odice三蒂丝': '#7B66FF',
  'ddg': '#3B82F6',
  'ONCUR安修泽': '#22C55E', // 改为更鲜艳的绿色
  'Aucuz奥札': '#92278F',
  'EVERBAB艾蓓拉': '#EC4899',
};

// 卡片主题色
const CARD_THEMES = {
  total: { bg: 'bg-blue-50', icon: 'text-blue-500', hover: 'hover:bg-blue-100/50' },
  spu: { bg: 'bg-purple-50', icon: 'text-purple-500', hover: 'hover:bg-purple-100/50' },
  average: { bg: 'bg-green-50', icon: 'text-green-500', hover: 'hover:bg-green-100/50' },
  period: { bg: 'bg-amber-50', icon: 'text-amber-500', hover: 'hover:bg-amber-100/50' }
};

interface LegendSelectChangedParams {
  name: string;
  selected: Record<string, boolean>;
  type: 'legendselectchanged';
}

export default function SkuCalendar() {
  const [searchParams] = useSearchParams();
  const chartRef = useRef<echarts.ECharts>();
  const searchId = searchParams.get('id') || '1043';
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  const { data, isLoading, error } = useProcessedSkuCalendarData(searchId);

  // 计算分页数据
  const paginatedItems = data.items.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const totalPages = Math.ceil(data.items.length / pageSize);

  useEffect(() => {
    // 初始化图表
    const chartDom = document.getElementById('skuChart');
    if (!chartDom) return;
    
    const myChart = echarts.init(chartDom);
    chartRef.current = myChart;
    
    // 更新图表数据
    if (data.charts.dailyTrend.length > 0) {
      updateChart(data.charts.dailyTrend);
    }

    // 添加窗口大小变化监听
    const handleResize = () => {
      myChart.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      myChart.dispose();
    };
  }, [data.charts.dailyTrend]);

  const updateChart = (chartData: any[]) => {
    if (!chartRef.current) return;

    // 按品牌分组数据，但保持每个SPU的独立数据点
    const brandGroups = chartData.reduce<Record<string, typeof chartData>>((acc, item) => {
      if (!acc[item.brandName]) {
        acc[item.brandName] = [];
      }
      acc[item.brandName].push(item);
      return acc;
    }, {});
    
    const option = {
      title: {
        text: '品牌排产数量趋势图',
        left: 'center',
        top: 10,
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: function(params: any) {
          return `品牌：${params.data.brandName}<br/>
                  SPU：${params.data.name}<br/>
                  SPU编码：${params.data.spuCode}<br/>
                  数量：${formatNumber(params.value[1])}<br/>
                  日期：${params.value[0]}`;
        }
      },
      legend: {
        top: 40,
        type: 'scroll',
        data: Object.keys(brandGroups),
        selected: Object.keys(brandGroups).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {} as Record<string, boolean>)
      },
      grid: {
        top: 100,
        left: 50,
        right: 50,
        bottom: 80,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: data.charts.allWeekDates,
        axisLabel: {
          formatter: formatDateWithWeek,
          rotate: 45,
          interval: 0,
          fontSize: 11,
          lineHeight: 14
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed'
          }
        }
      },
      yAxis: {
        type: 'value',
        name: '排产数量',
        axisLabel: {
          formatter: (value: number) => formatNumber(value)
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed'
          }
        }
      },
      series: Object.entries(brandGroups).map(([brandName, items]) => ({
        name: brandName,
        type: 'scatter',
        symbolSize: 15,
        itemStyle: {
          color: BRAND_COLORS[brandName as keyof typeof BRAND_COLORS] || '#666',
          shadowBlur: 10,
          shadowColor: 'rgba(0, 0, 0, 0.2)'
        },
        emphasis: {
          focus: 'series',
          scale: true,
          scaleSize: 6,
          itemStyle: {
            shadowBlur: 20,
            borderColor: '#fff',
            borderWidth: 2,
            opacity: 1
          }
        },
        blur: {
          itemStyle: {
            opacity: 0.2
          }
        },
        data: items.map(item => ({
          value: [item.date, item.value],
          name: item.name,
          brandName: item.brandName,
          spuCode: item.spuCode
        }))
      }))
    };

    // 添加图例点击事件监听器
    chartRef.current.off('legendselectchanged');
    chartRef.current.on('legendselectchanged', function(params: unknown) {
      const selected = (params as any).selected;
      const chart = chartRef.current;
      if (!chart) return;
      
      const option = chart.getOption();
      const series = option.series as any[];

      // 更新所有系列的样式
      series.forEach((s: any) => {
        if (selected[s.name]) {
          s.itemStyle.opacity = 1;
          s.emphasis.itemStyle.opacity = 1;
        } else {
          s.itemStyle.opacity = 0.1;
          s.emphasis.itemStyle.opacity = 0.1;
        }
      });

      chart.setOption(option);
    });

    chartRef.current.setOption(option);
  };

  const formatDate = (dateStr: string) => {
    return dateStr.split('T')[0];
  };

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="bg-red-50">
          <CardContent className="p-6">
            <div className="text-red-600">加载数据失败: {error.message}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* 数据概览卡片 */}
      <div className="grid grid-cols-4 gap-6">
        <Card className={`transition-colors ${CARD_THEMES.total.bg} border-none`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Box className={`h-4 w-4 ${CARD_THEMES.total.icon}`} />
              总计划数量
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : formatNumber(data.summary.totalQuantity)}
            </div>
            <p className="text-xs text-slate-500 mt-1">所有品牌计划总量</p>
          </CardContent>
        </Card>

        <Card className={`transition-colors ${CARD_THEMES.spu.bg} border-none`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Package className={`h-4 w-4 ${CARD_THEMES.spu.icon}`} />
              SPU数量
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : formatNumber(data.summary.totalCount)}
            </div>
            <p className="text-xs text-slate-500 mt-1">计划SPU总数</p>
          </CardContent>
        </Card>

        <Card className={`transition-colors ${CARD_THEMES.average.bg} border-none`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className={`h-4 w-4 ${CARD_THEMES.average.icon}`} />
              平均排产量
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : 
                formatNumber(Math.round(data.summary.totalQuantity / data.summary.totalCount))}
            </div>
            <p className="text-xs text-slate-500 mt-1">每个SPU平均排产量</p>
          </CardContent>
        </Card>

        <Card className={`transition-colors ${CARD_THEMES.period.bg} border-none`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Box className={`h-4 w-4 ${CARD_THEMES.period.icon}`} />
              计划周期
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : `${data.charts.allWeekDates.length} 周`}
            </div>
            <p className="text-xs text-slate-500 mt-1">总排产周期跨度</p>
          </CardContent>
        </Card>
      </div>

      {/* 图表区域 */}
      <Card className="border-none bg-gradient-to-br from-white to-slate-50">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-800">品牌排产趋势分析</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div id="skuChart" style={{ width: '100%', height: '500px' }}></div>
        </CardContent>
      </Card>

      {/* 表格区域 */}
      <Card className="border-none bg-white">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-800">排产明细数据</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="rounded-md border border-slate-100">
            <Table>
              <TableHeader className="sticky top-0 bg-slate-50 z-10">
                <TableRow>
                  {TABLE_HEADERS.map(header => (
                    <TableHead key={header.key} className="bg-slate-50 font-medium text-slate-700">
                      {header.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={TABLE_HEADERS.length} className="text-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
                      <div className="mt-2 text-sm text-slate-500">加载中...</div>
                    </TableCell>
                  </TableRow>
                ) : paginatedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={TABLE_HEADERS.length} className="text-center text-slate-500">
                      暂无数据
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedItems.map((row, index) => (
                    <TableRow 
                      key={index} 
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <TableCell className="font-medium text-slate-700">{row.brandId}</TableCell>
                      <TableCell className="text-slate-700">{row.brandName}</TableCell>
                      <TableCell className="font-mono text-slate-600">{row.spuCode}</TableCell>
                      <TableCell className="text-slate-700">{row.spuName}</TableCell>
                      <TableCell className="text-slate-600">{formatDate(row.planDate)}</TableCell>
                      <TableCell className="font-medium text-slate-900">{formatNumber(row.planQuantity)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* 分页控件 */}
          <div className="mt-4 flex justify-center">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(p => Math.max(1, p - 1));
                    }}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => (
                  <PaginationItem key={i}>
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentPage(i + 1);
                      }}
                      isActive={currentPage === i + 1}
                      className="hover:bg-blue-50"
                    >
                      {i + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                {totalPages > 5 && (
                  <>
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(totalPages);
                        }}
                        isActive={currentPage === totalPages}
                        className="hover:bg-blue-50"
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  </>
                )}
                <PaginationItem>
                  <PaginationNext 
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(p => Math.min(totalPages, p + 1));
                    }}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 